import { useQuery } from '@apollo/client';
import { Exchanges } from '@generated/graphql';
import type { FinancialProduct } from '@interfaces/financialProduct';
import { Chip } from '@ovComponents/2-component/chip/chip';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { Pagination } from '@ovComponents/2-component/pagination/pagination';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import {
  Table,
  TableBody,
  TableCell,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { AssetClassSelect } from '@ovComponents/3-pattern/assetClassSelect/assetClassSelect';
import { FilterModal } from '@ovComponents/3-pattern/filterModal/filterModal';
import { UserContext } from '@providers/userContextProvider';
import { enumValues } from '@utils/enumValues';
import useTextDebounce from '@utils/useTextDebounce';
import { Box, Button, Skeleton, Typography, useTheme } from 'hephaestus/web';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../hooks/useLocalization';
import { usePermission } from '../../hooks/usePermission';
import { SEARCH_SHELF } from './addSecuritiesDialog.query';
import { ShelfSecurityItem } from './ShelfSecurityItem';

const PAGE_SIZE = 15;
const ANY = 'ANY';

export type AddSecuritiesDialogProp = {
  open: boolean;
  onClose: () => void;
  onAdd: (securities: FinancialProduct[]) => void;
  /** Products already in the model: shown, ticked and locked. */
  inModelProductIds: string[];
};

/**
 * The product shelf as a picker: searched and paged on the server, so a shelf
 * of any size can be reached. Picks survive a new search, filter or page.
 */
export const AddSecuritiesDialog = ({
  open,
  onClose,
  onAdd,
  inModelProductIds,
}: AddSecuritiesDialogProp) => {
  const { t } = useTranslation([
    'modelPortfolios',
    'configureModels',
    'components',
    'shared',
  ]);
  const theme = useTheme();
  const { translateBackendString } = useLocalization();
  const { activeOrganization } = useContext(UserContext);
  const canReadFinancialProducts = usePermission('read:financial_products');
  const [immediateSearch, setImmediateSearch] = useState('');
  const searchText = useTextDebounce({ value: immediateSearch, delay: 500 });
  const [exchange, setExchange] = useState(ANY);
  const [primaryAssetClassId, setPrimaryAssetClassId] = useState(ANY);
  const [secondaryAssetClassId, setSecondaryAssetClassId] = useState(ANY);
  const [page, setPage] = useState(1);
  const [picked, setPicked] = useState<FinancialProduct[]>([]);

  const inModel = useMemo(
    () => new Set(inModelProductIds),
    [inModelProductIds],
  );
  const exchanges = enumValues(Exchanges).sort();

  const { data, loading, error } = useQuery(SEARCH_SHELF, {
    skip: !open || !activeOrganization.id || !canReadFinancialProducts,
    // A cache hit does not supersede a request still in flight, so a late answer for older filters would replace the current one.
    fetchPolicy: 'network-only',
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization.id,
          searchText: searchText || undefined,
          exchange: exchange !== ANY ? exchange : undefined,
          primaryAssetClassId:
            primaryAssetClassId !== ANY ? primaryAssetClassId : undefined,
          secondaryAssetClassId:
            secondaryAssetClassId !== ANY ? secondaryAssetClassId : undefined,
        },
        pagination: {
          sortField: 'ticker',
          sortDesc: false,
          perPage: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
        },
      },
    },
  });

  // The model table's rows are typed with the legacy FinancialProduct shape.
  const products = (data?.fetchFinancialProducts?.financialProducts ??
    []) as FinancialProduct[];
  const reportedTotal = data?.fetchFinancialProducts?.totalCount;
  // Held through a fetch, so the pager does not unmount while the next page loads.
  const [totalCount, setTotalCount] = useState(0);
  useEffect(() => {
    if (reportedTotal == null) return;
    setTotalCount(reportedTotal);
    // A page picked against the previous results can sit past the end of these.
    const lastPage = Math.max(1, Math.ceil(reportedTotal / PAGE_SIZE));
    if (page > lastPage) setPage(lastPage);
  }, [reportedTotal, page]);
  const isPicked = (product: FinancialProduct) =>
    picked.some((p) => p.id === product.id);

  const toggle = (product: FinancialProduct) => {
    if (!product.id || inModel.has(product.id)) return;
    setPicked((current) =>
      current.some((p) => p.id === product.id)
        ? current.filter((p) => p.id !== product.id)
        : [...current, product],
    );
  };

  const close = () => {
    setImmediateSearch('');
    setExchange(ANY);
    setPrimaryAssetClassId(ANY);
    setSecondaryAssetClassId(ANY);
    setPage(1);
    setPicked([]);
    setTotalCount(0);
    onClose();
  };

  const statusRow = (text: string, testId: string) => (
    <TableRow>
      <TableCell colSpan={8} data-testid={testId}>
        <Typography variant='bodySmall' component='p'>
          {text}
        </Typography>
      </TableCell>
    </TableRow>
  );

  const renderRows = () => {
    if (error) {
      return statusRow(
        t('modelPortfolios:addSecuritiesDialog.loadError'),
        'add-securities-error',
      );
    }
    if (loading) {
      return [...Array(PAGE_SIZE)].map((_, rowIndex) => (
        <TableRow key={rowIndex} data-testid='add-securities-loading-row'>
          {[...Array(8)].map((_, cellIndex) => (
            <TableCell key={cellIndex}>
              <Skeleton width='100%' />
            </TableCell>
          ))}
        </TableRow>
      ));
    }
    if (products.length === 0) {
      return statusRow(
        t('modelPortfolios:addSecuritiesDialog.noResults'),
        'add-securities-empty',
      );
    }
    return products.map((product) => (
      <ShelfSecurityItem
        key={product.id}
        security={product}
        picked={isPicked(product)}
        inModel={inModel.has(product.id ?? '')}
        onToggle={toggle}
      />
    ));
  };

  return (
    <Dialog open={open} onClose={close} maxWidth='lg' fullWidth>
      <DialogTitle onClose={close}>
        {t('modelPortfolios:addSecuritiesDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            my: 1,
          }}
        >
          <TextField
            label=''
            fullWidth
            sx={{ width: '300px' }}
            placeholder={t('modelPortfolios:addSecuritiesDialog.search')}
            value={immediateSearch}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setImmediateSearch(event.target.value);
              setPage(1);
            }}
            leadingIcon='search'
            testId='add-securities-search'
          />
          <FilterModal
            filterExists={[
              exchange,
              primaryAssetClassId,
              secondaryAssetClassId,
            ].some((value) => value !== ANY)}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <SelectField
                label={t('configureModels:filters.exchange')}
                fullWidth
                value={exchange}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setExchange(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem key={ANY} value={ANY}>
                  {t('components:any')}
                </MenuItem>
                {exchanges.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </SelectField>
              <AssetClassSelect
                value={primaryAssetClassId}
                omitAny={false}
                setAssetClass={(e) => {
                  setPrimaryAssetClassId(e.target.value);
                  setPage(1);
                }}
                label={t('configureModels:filters.primaryAssetClass')}
              />
              <AssetClassSelect
                value={secondaryAssetClassId}
                omitAny={false}
                setAssetClass={(e) => {
                  setSecondaryAssetClassId(e.target.value);
                  setPage(1);
                }}
                label={t('configureModels:filters.secondaryAssetClass')}
              />
            </Box>
          </FilterModal>
        </Box>
        {picked.length > 0 && (
          <Box
            data-testid='add-securities-selection'
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              mb: 1,
              px: 1.5,
              py: 0.75,
              border: '1px solid',
              borderColor: theme.color.primary,
              borderRadius: '4px',
              bgcolor: theme.color.primaryVariant,
            }}
          >
            <Typography variant='labelSmall'>
              {t('modelPortfolios:addSecuritiesDialog.selected', {
                count: picked.length,
              })}
            </Typography>
            {picked.map((product) => (
              <Chip
                key={product.id}
                small
                text={
                  product.ticker ||
                  translateBackendString(product.translatedName)
                }
                onRemove={() => toggle(product)}
              />
            ))}
            <Button variant='text' size='small' onClick={() => setPicked([])}>
              {t('modelPortfolios:addSecuritiesDialog.clearSelection')}
            </Button>
          </Box>
        )}
        <Box sx={{ overflowX: 'auto' }}>
          <Table aria-label={t('modelPortfolios:addSecuritiesDialog.title')}>
            <TableBody>
              <TableRow>
                <TableHeadCell />
                <TableHeadCell>
                  {t('configureModels:table.ticker')}
                </TableHeadCell>
                <TableHeadCell>{t('configureModels:table.name')}</TableHeadCell>
                <TableHeadCell>
                  {t('configureModels:table.exchange')}
                </TableHeadCell>
                <TableHeadCell>
                  {t('configureModels:table.primaryAssetClass')}
                </TableHeadCell>
                <TableHeadCell>
                  {t('configureModels:table.secondaryAssetClass')}
                </TableHeadCell>
                <TableHeadCell>
                  {t('configureModels:table.currency')}
                </TableHeadCell>
                <TableHeadCell right>
                  {t('configureModels:table.currentPrice')}
                </TableHeadCell>
              </TableRow>
              {renderRows()}
            </TableBody>
          </Table>
        </Box>
        {totalCount > PAGE_SIZE && (
          <Pagination
            count={Math.ceil(totalCount / PAGE_SIZE)}
            page={page}
            disabled={loading}
            onChange={(_e, newPage) => setPage(newPage)}
            sx={{
              p: 1,
              textAlign: 'right',
              '.MuiPagination-ul': { justifyContent: 'end' },
            }}
          />
        )}
      </DialogContent>
      <DialogFooter>
        <Button variant='outlined' onClick={close}>
          {t('shared:cancel')}
        </Button>
        <Button
          variant='contained'
          disabled={picked.length === 0}
          onClick={() => {
            onAdd(picked);
            close();
          }}
          data-testid='add-securities-done'
        >
          {t('shared:done')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
